/**
 * OnboardingChecklist Component
 *
 * Persistent checklist that guides users through initial setup tasks.
 * Displays in sidebar or dashboard until all tasks are complete.
 */
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Check,
  ChevronDown,
  ChevronUp,
  Package,
  Warehouse,
  Link as LinkIcon,
  BarChart3,
  Users,
  X,
  Gift
} from 'lucide-react';
import api from '../../services/api';
import { trackEvent } from '../../services/telemetry';

const CHECKLIST_ITEMS = [
  {
    id: 'add-product',
    title: 'Catalog your first SKU',
    description: 'Define what exists before the system can monitor its state',
    icon: Package,
    link: '/inventory/products/new',
    reward: 'Activate stock state monitoring'
  },
  {
    id: 'setup-warehouse',
    title: 'Establish a storage location',
    description: 'Create the physical context the system needs to track stock movement',
    icon: Warehouse,
    link: '/warehouses/new',
    reward: 'Enable stock movement and location tracking'
  },
  {
    id: 'connect-channel',
    title: 'Connect a sales channel',
    description: 'Bring channel orders and inventory state into a single operational view',
    icon: LinkIcon,
    link: '/integrations',
    reward: 'Maintain continuous state sync across channels'
  },
  {
    id: 'invite-team',
    title: 'Invite a team member',
    description: 'Grant operational access to your team',
    icon: Users,
    link: '/settings/team',
    reward: 'Enable multi-user operational access'
  },
  {
    id: 'view-analytics',
    title: 'Review your operational baseline',
    description: 'Validate that the system reflects your actual operational state',
    icon: BarChart3,
    link: '/analytics',
    reward: 'Operational visibility across inventory, orders, and procurement'
  }
];

const OnboardingChecklist = ({ variant = 'card', onDismiss }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [completedItems, setCompletedItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    fetchProgress();

    // Poll for progress updates every 30 seconds to catch auto-completions
    const pollInterval = setInterval(fetchProgress, 30000);
    return () => clearInterval(pollInterval);
  }, []);

  // Also refresh when window regains focus (user may have completed a task in another tab)
  useEffect(() => {
    const handleFocus = () => fetchProgress();
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  const fetchProgress = async () => {
    try {
      const response = await api.get('/onboarding/progress');
      const { completedItems: items, dismissed: isDismissed } = response.data;
      setCompletedItems(items || []);
      if (isDismissed) setDismissed(true);
    } catch (error) {
      // Initialize with empty if endpoint doesn't exist yet
      setCompletedItems([]);
    } finally {
      setLoading(false);
    }
  };

  const markComplete = async (itemId) => {
    if (completedItems.includes(itemId)) return;

    try {
      await api.post('/onboarding/complete-item', { itemId });
      setCompletedItems(prev => [...prev, itemId]);
      trackEvent('onboarding_item_completed', { itemId });
    } catch (error) {
      console.error('Failed to mark item complete:', error);
    }
  };

  const handleDismiss = async () => {
    try {
      await api.post('/onboarding/dismiss');
      setDismissed(true);
      trackEvent('onboarding_checklist_dismissed', {
        completedCount: completedItems.length,
        totalCount: CHECKLIST_ITEMS.length
      });
      onDismiss?.();
    } catch (error) {
      setDismissed(true);
    }
  };

  if (dismissed || loading) return null;

  const progress = (completedItems.length / CHECKLIST_ITEMS.length) * 100;
  const allComplete = completedItems.length === CHECKLIST_ITEMS.length;

  // Compact variant for sidebar
  if (variant === 'compact') {
    return (
      <div className="mx-3 mb-4 p-3 bg-gradient-to-br from-brand-50 to-brand-50 dark:from-brand-900/20 dark:to-brand-900/20 rounded-xl border border-brand-100 dark:border-brand-800">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium text-brand-700 dark:text-brand-300">
            Operational Baseline
          </span>
          <span className="text-xs text-brand-600 dark:text-brand-400">
            {completedItems.length}/{CHECKLIST_ITEMS.length}
          </span>
        </div>
        <div className="h-1.5 bg-brand-100 dark:bg-brand-900/50 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-brand-500 to-brand-400 transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>
        {!allComplete && (
          <Link
            to="/getting-started"
            className="mt-2 block text-xs text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300"
          >
            View checklist →
          </Link>
        )}
      </div>
    );
  }

  // Full card variant
  return (
    <div className="bg-white dark:bg-dark-800 rounded-xl border border-gray-200 dark:border-dark-700 shadow-sm overflow-hidden">
      {/* Header */}
      <div
        className="p-4 bg-gradient-to-r from-brand-50 to-brand-50 dark:from-brand-900/20 dark:to-brand-900/20 cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-brand-500 flex items-center justify-center">
              <Gift className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white">
                Operational Baseline
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Establish the data foundations the system requires
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <span className="text-sm font-medium text-brand-600 dark:text-brand-400">
                {completedItems.length}/{CHECKLIST_ITEMS.length}
              </span>
              <div className="w-24 h-1.5 bg-gray-200 dark:bg-dark-700 rounded-full overflow-hidden mt-1">
                <div
                  className="h-full bg-brand-500 transition-all duration-500"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
            <button className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
              {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </button>
          </div>
        </div>
      </div>

      {/* Checklist items */}
      {isExpanded && (
        <div className="p-4 space-y-2">
          {CHECKLIST_ITEMS.map((item) => {
            const Icon = item.icon;
            const isComplete = completedItems.includes(item.id);

            return (
              <Link
                key={item.id}
                to={item.link}
                onClick={() => markComplete(item.id)}
                className={`flex items-center gap-3 p-3 rounded-lg transition-all ${
                  isComplete
                    ? 'bg-green-50 dark:bg-green-900/20'
                    : 'bg-gray-50 dark:bg-dark-700 hover:bg-gray-100 dark:hover:bg-dark-600'
                }`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                  isComplete
                    ? 'bg-green-500 text-white'
                    : 'bg-gray-200 dark:bg-dark-600 text-gray-400'
                }`}>
                  {isComplete ? <Check size={16} /> : <Icon size={16} />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium ${
                    isComplete
                      ? 'text-green-700 dark:text-green-400 line-through'
                      : 'text-gray-900 dark:text-white'
                  }`}>
                    {item.title}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {item.description}
                  </p>
                </div>
                {!isComplete && (
                  <span className="text-xs text-brand-600 dark:text-brand-400 bg-brand-50 dark:bg-brand-900/30 px-2 py-1 rounded-full">
                    +{item.reward}
                  </span>
                )}
              </Link>
            );
          })}

          {/* Dismiss option */}
          {allComplete ? (
            <div className="pt-3 text-center">
              <button
                onClick={handleDismiss}
                className="text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
              >
                Hide checklist
              </button>
            </div>
          ) : (
            <div className="pt-3 flex justify-between items-center">
              <button
                onClick={handleDismiss}
                className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 flex items-center gap-1"
              >
                <X size={12} />
                Dismiss
              </button>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                Complete all steps to establish full operational state
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default OnboardingChecklist;
